import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Instagram, Loader2 } from "lucide-react";
import { toast } from "sonner";

const POSTS = [
  {
    slug: "cybersecyyc-brand",
    url: "https://static.prod-images.emergentagent.com/jobs/b218b4d1-caa1-497b-b1ec-28622826bca6/images/e7dd27cf56fd1fbe68dc6cd050674556574b9ba2f0017ba0f37cd5f769b1eb41.jpeg",
    alt: "CyberSecYYC brand poster — cybersecurity without the hoodies",
    caption: "the brand, in one post",
    rotate: "rotate-[-1.5deg]",
  },
  {
    slug: "scam-alert",
    url: "https://static.prod-images.emergentagent.com/jobs/b218b4d1-caa1-497b-b1ec-28622826bca6/images/3afd7db5c2fe6b6ac14cf7893b74225727c8baff7b0e35ddac7bd928db77aabd.jpeg",
    alt: "Scam Alert post — the Hi Mom new number text is fake",
    caption: "weekly scam alert",
    rotate: "rotate-[1.2deg]",
  },
  {
    slug: "yyc-cyber-tip",
    url: "https://static.prod-images.emergentagent.com/jobs/b218b4d1-caa1-497b-b1ec-28622826bca6/images/049fc18b3d4f31ab7bd2af059c396afce2cc9c609bc4352d959ff20156110b75.jpeg",
    alt: "YYC Cyber Tip post — turn on two-step login",
    caption: "two-minute tips",
    rotate: "rotate-[-1deg]",
  },
  {
    slug: "neighbourhood-watch",
    url: "https://static.prod-images.emergentagent.com/jobs/b218b4d1-caa1-497b-b1ec-28622826bca6/images/66415f22a9709a49e92e22c8e0b0d7338371ec484c73fa12855bbcc1a5ce10b6.jpeg",
    alt: "The Digital Neighbourhood Watch — Calgary edition post",
    caption: "community pride",
    rotate: "rotate-[1.5deg]",
  },
];

export default function FeedGallery() {
  const [downloading, setDownloading] = useState(null);

  const downloadImage = async (post) => {
    setDownloading(post.slug);
    try {
      const res = await fetch(post.url);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${post.slug}.jpeg`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Saved! Share it with your crew.");
    } catch {
      window.open(post.url, "_blank", "noopener");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section id="feed" className="py-24 lg:py-32 bg-muted/50 border-y border-border scroll-mt-20" data-testid="feed-gallery-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap items-end justify-between gap-8 mb-16"
        >
          <div className="max-w-2xl">
            <p className="font-hand text-2xl text-primary mb-3">fresh off the feed</p>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight" data-testid="feed-gallery-heading">
              Posts made to be <span className="text-primary">shared, not scrolled past.</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed">
              Grab any of these and send them to someone who needs the heads-up — or repost them to
              your own story. Spreading the word is the whole point.
            </p>
          </div>
          <a
            href="https://www.instagram.com/cybersecyyc/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="feed-instagram-button"
            className="inline-flex items-center gap-2 border-2 border-foreground font-display font-medium px-7 py-3.5 rounded-full hover:bg-foreground hover:text-background transition-colors duration-200"
          >
            <Instagram className="w-4 h-4" strokeWidth={2} />
            See the full feed
          </a>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6" data-testid="feed-gallery-grid">
          {POSTS.map((post, i) => (
            <motion.figure
              key={post.slug}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className={`group relative ${post.rotate} hover:rotate-0 transition-transform duration-300`}
              data-testid={`feed-post-${i}`}
            >
              <div className="rounded-2xl overflow-hidden border border-border shadow-sm bg-card aspect-square">
                <img src={post.url} alt={post.alt} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <button
                onClick={() => downloadImage(post)}
                aria-label={`Download ${post.caption} post`}
                data-testid={`feed-download-${i}`}
                className="absolute top-3 right-3 bg-background/95 text-foreground rounded-full p-2.5 shadow-md opacity-0 group-hover:opacity-100 focus:opacity-100 hover:scale-110 transition-[opacity,transform] duration-200"
              >
                {downloading === post.slug ? (
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                ) : (
                  <Download className="w-4 h-4" strokeWidth={2} />
                )}
              </button>
              <figcaption className="mt-3 font-hand text-xl text-muted-foreground text-center">
                {post.caption}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
